import React, { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useGetSubscription, Subscription, Plan } from "../api/billing";
import axios from "../api/axios";
import { getCurrentFleetId } from "../utils/getCurrentFleet";

/**
 * Fallback feature -> plan-name map.
 *
 * DEPRECATED as a source of truth. The real answer comes from
 * GET /billing/feature-matrix, which reports the cheapest ACTIVE plan actually
 * selling each feature. This constant is only a last resort for the moment
 * before that request resolves, or if it fails.
 *
 * It has to be a fallback rather than the answer because plans are
 * administrator-managed: rename Performance, move geo-location into Essential,
 * or add a tier, and every entry below becomes a lie that tells the customer to
 * buy the wrong thing.
 */
export const FEATURE_PLAN_MAP: Record<string, string> = {
  vehicle_management: "Essential",
  driver_management: "Essential",
  maintenance_tracking: "Essential",
  documents: "Essential",
  geo_location: "Performance",
  eco_driving: "Performance",
  tax_optimization: "Optimization",
  advanced_analytics: "Optimization",
  fleet_qa: "AI Assistant",
  user_qa: "AI Assistant",
  ai_recommendations: "AI Assistant",
  weekly_summaries: "AI Assistant",
  smart_alerts: "AI Assistant",
  automatic_follow_ups: "Fleet Manager Copilot",
  maintenance_coordination: "Fleet Manager Copilot",
  driver_communication: "Fleet Manager Copilot",
  daily_fleet_brief: "Fleet Manager Copilot",
  autonomous_actions: "Autonomous Fleet",
  ai_driven_workflows: "Autonomous Fleet",
  proactive_fleet_optimization: "Autonomous Fleet",
};

/** feature key -> cheapest active plan that sells it. */
interface FeatureMatrixEntry {
  slug: string;
  name: string;
  tier: string;
  price_cents_annual: number;
}

interface FeatureMatrixResponse {
  matrix: Record<string, FeatureMatrixEntry>;
  labels: Record<string, string>;
}

export const ROUTE_FEATURE_MAP: Record<string, string> = {
  "/geolocalisation": "geo_location",
  "/ai-agent": "fleet_qa",
  "/fiscalite": "tax_optimization",
  "/analyses": "advanced_analytics",
};

interface SubscriptionContextValue {
  subscription: Subscription | null;
  plan: Plan | null;
  aiAddon: Plan | null;
  isLoading: boolean;
  isActive: boolean;
  hasFeature: (feature: string) => boolean;
  vehicleQuota: number;
  planSlug: string | null;
  requiresUpgrade: (feature: string) => string | null;
  /** Human label for a feature key, from the server catalogue. */
  featureLabel: (feature: string) => string;
}

const SubscriptionContext = createContext<SubscriptionContextValue>({
  subscription: null,
  plan: null,
  aiAddon: null,
  isLoading: true,
  isActive: false,
  hasFeature: () => false,
  vehicleQuota: 0,
  planSlug: null,
  requiresUpgrade: () => null,
  featureLabel: (feature) => feature,
});

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const companyId = getCurrentFleetId();
  const { subscription, isPending } = useGetSubscription(companyId);

  /*
   * Which plan sells which feature is decided by the admin panel, so it is
   * fetched, not compiled in. Public endpoint, cached for a while: the answer
   * only changes when an administrator edits an offer.
   */
  const { data: featureMatrix } = useQuery({
    queryKey: ["billing", "feature-matrix"],
    queryFn: async (): Promise<FeatureMatrixResponse> => {
      const response = await axios.get("/billing/feature-matrix");
      return response.data?.data ?? response.data;
    },
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  const value = useMemo<SubscriptionContextValue>(() => {
    const plan = subscription?.plan ?? null;
    const aiAddon = subscription?.ai_addon ?? null;

    // Prefer the API-computed effective features (SaaS plan with
    // "everything_in_*" inheritance expanded + AI add-on merged).
    // Fall back to raw plan.features for older API responses.
    const features: string[] = Array.isArray(subscription?.features)
      ? (subscription!.features as string[])
      : plan
      ? Array.isArray(plan.features)
        ? plan.features
        : JSON.parse((plan.features as any) || "[]")
      : [];

    const isActive =
      subscription?.status === "active" || subscription?.status === "trialing";

    const hasFeature = (feature: string): boolean => {
      if (!isActive) return false;
      return features.includes(feature);
    };

    const requiresUpgrade = (feature: string): string | null => {
      if (hasFeature(feature)) return null;

      // Server first: names the plan that actually sells this today.
      const fromServer = featureMatrix?.matrix?.[feature]?.name;
      if (fromServer) return fromServer;

      // Then the stale local map, then a generic prompt. Never invent a plan
      // name: telling a customer to buy "Performance" when that tier was
      // renamed sends them looking for something that no longer exists.
      return FEATURE_PLAN_MAP[feature] ?? null;
    };

    const featureLabel = (feature: string): string =>
      featureMatrix?.labels?.[feature] ?? feature;

    return {
      subscription,
      plan,
      aiAddon,
      isLoading: isPending,
      isActive,
      hasFeature,
      vehicleQuota: subscription?.vehicle_quantity ?? 0,
      planSlug: plan?.slug ?? null,
      requiresUpgrade,
      featureLabel,
    };
  }, [subscription, isPending, featureMatrix]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
